<style scoped>
/*
 * Densidade: a tela é uma fila varrida sob pressão, com o cliente esperando.
 * Linha baixa e fonte de 12px cabem ~18 registros em 1440x900, contra ~9 antes.
 */
.tabela-pix {
  font-size: 12px;
}

.tabela-pix td {
  padding: 6px 10px;
  vertical-align: middle;
}

.tabela-pix th {
  padding: 8px 10px;
  font-size: 10px;
  font-weight: bold;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: #667085;
}

/*
 * A hierarquia de urgência que faltava: só os três estados que pedem decisão
 * ganham faixa lateral e fundo tingido. Os de repouso ficam brancos — as seis
 * cores competiam em igualdade e a fila não tinha relevo.
 */
.linha-pix {
  border-left: 3px solid transparent;
}

.linha-pix.is-urgente-3 {
  border-left-color: #b42318;
  background: #fffbfa;
}

.linha-pix.is-urgente-2 {
  border-left-color: #f97066;
  background: #fffdfd;
}

.linha-pix.is-urgente-1 {
  border-left-color: #fdb022;
  background: #fffefa;
}

.pedido-link {
  font-weight: bold;
  letter-spacing: 0.2px;
  background: none;
  border: 0;
  padding: 0;
  color: #3a3e81;
  cursor: pointer;
}

.pedido-link:hover {
  text-decoration: underline;
}

.col-valor {
  text-align: right;
  font-weight: 600;
  color: #101828;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.col-cliente {
  max-width: 280px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.col-data,
.col-origem {
  color: #667085;
  white-space: nowrap;
}

/*
 * Badge com ponto colorido: os estados de repouso viram contorno cinza e só o
 * ponto carrega a cor, então a mesma semântica pesa menos na varredura.
 */
.badge-status {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 8px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}

.badge-status__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex: none;
}

.badge-status.is-devolucao_nao_confirmada {
  background: #b42318;
  border: 1px solid #b42318;
  color: #fff;
}

.badge-status.is-falha_integracao {
  background: #fef3f2;
  border: 1px solid #fecdca;
  color: #b42318;
}

.badge-status.is-pago_nao_integrado {
  background: #fffaeb;
  border: 1px solid #fedf89;
  color: #b54708;
}

.badge-status.is-pago_integrado,
.badge-status.is-devolvido,
.badge-status.is-aguardando {
  background: #fff;
  border: 1px solid #e4e7ec;
  color: #667085;
}

/*
 * O motivo é a informação que mais importa em falha_integracao e era a menos
 * legível: 10px vermelho truncado em 200px. Agora usa o tamanho da tabela e
 * quebra em duas linhas antes de cortar.
 */
.motivo {
  color: #475467;
  line-height: 1.35;
  max-width: 260px;
  overflow-wrap: break-word;
  word-break: break-word;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.motivo.is-falha {
  color: #912018;
}

.motivo.is-vazio {
  color: #c4c9d2;
}

.skeleton-linha {
  display: grid;
  grid-template-columns: 90px 1fr 120px 90px 100px 150px;
  gap: 10px;
  padding: 9px 10px;
  border-bottom: 1px solid #f2f4f7;
}

.skeleton-barra {
  height: 10px;
  border-radius: 4px;
  background: #e4e7ec;
  animation: pulsePix 1.4s ease-in-out infinite;
}

.skeleton-barra.is-fraca {
  background: #eef0f4;
}

@keyframes pulsePix {
  0%,
  100% {
    opacity: 0.55;
  }
  50% {
    opacity: 1;
  }
}

.erro-lista {
  border: 1px solid #fecdca;
  background: #fef3f2;
  border-radius: 8px;
  padding: 14px 16px;
  margin: 12px 0;
  display: flex;
  gap: 12px;
}

.erro-lista__titulo {
  font-size: 13px;
  font-weight: bold;
  color: #b42318;
}

.erro-lista__texto {
  font-size: 12px;
  color: #912018;
  line-height: 1.5;
  margin-top: 4px;
}
</style>

<template>
  <div>
    <div v-if="carregando">
      <div v-for="n in 8" :key="`sk-${n}`" class="skeleton-linha">
        <div class="skeleton-barra"></div>
        <div class="skeleton-barra is-fraca"></div>
        <div class="skeleton-barra is-fraca"></div>
        <div class="skeleton-barra"></div>
        <div class="skeleton-barra is-fraca"></div>
        <div class="skeleton-barra is-fraca"></div>
      </div>
    </div>

    <div v-else-if="erro" class="erro-lista">
      <fa-icon icon="exclamation-triangle" style="color: #b42318" />
      <div>
        <div class="erro-lista__titulo">Não foi possível carregar a fila</div>
        <div class="erro-lista__texto">
          {{ erro }} Nenhum pagamento foi alterado.
        </div>
        <button
          class="button button-error"
          type="button"
          style="margin-top: 10px"
          v-on:click="recarregar"
        >
          Tentar de novo
        </button>
      </div>
    </div>

    <empty-state
      v-else-if="pedidos.length === 0"
      icon="check-circle"
      title="Nenhum pagamento nesta situação"
      description="Não há pedidos PIX no período e no status selecionados."
    />

    <div v-else class="table-responsive">
      <table class="table table-hover tabela-pix">
        <thead>
          <tr>
            <th>Pedido</th>
            <th>Cliente</th>
            <th>Data</th>
            <th style="text-align: right">Valor</th>
            <th>Origem</th>
            <th>Situação</th>
            <th>Motivo / diagnóstico</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="pedido in pedidos"
            :key="pedido.id"
            class="linha-pix"
            :class="`is-urgente-${urgencia(pedido)}`"
          >
            <td>
              <button
                class="pedido-link"
                type="button"
                v-on:click="openInfo(pedido)"
              >
                {{ pedido.id }}
              </button>
            </td>
            <td class="col-cliente" :title="pedido.cliente.razSoc">
              {{ pedido.cliente.razSoc }}
            </td>
            <td class="col-data">{{ convertDataHora(pedido.data) }}</td>
            <td class="col-valor">{{ pedido.valor | monetary }}</td>
            <td class="col-origem">{{ pedido.origem }}</td>
            <td>
              <span class="badge-status" :class="`is-${pedido.status.chave}`">
                <span
                  class="badge-status__dot"
                  :style="{ background: corDoPonto(pedido) }"
                ></span>
                {{ pedido.status.label }}
              </span>
            </td>
            <td>
              <div
                class="motivo"
                :class="classeDoMotivo(pedido)"
                :title="resumoDoMotivo(pedido)"
              >
                {{ pedido.motivo || '—' }}
              </div>
            </td>
            <td class="actions">
              <button
                class="button button-info"
                type="button"
                title="Detalhes"
                v-on:click="openInfo(pedido)"
              >
                <fa-icon icon="info-circle" /> Detalhes
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import EmptyState from '../../components/EmptyState';

/**
 * Peso visual de cada estado na fila: 3 é o pior caso (cliente sem produto e
 * sem dinheiro), 0 é estado de repouso, que não pede decisão nenhuma.
 *
 * Só define aparência. Quem decide qual ação cada estado libera continua sendo
 * `statusPix.js`, e as ações agora vivem no modal.
 */
const URGENCIA = {
  devolucao_nao_confirmada: 3,
  falha_integracao: 2,
  pago_nao_integrado: 1,
  pago_integrado: 0,
  devolvido: 0,
  aguardando: 0,
};

const COR_DO_PONTO = {
  devolucao_nao_confirmada: '#fff',
  falha_integracao: '#dd514c',
  pago_nao_integrado: '#f9b115',
  pago_integrado: '#2eb85c',
  devolvido: '#6c757d',
  aguardando: '#adb5bd',
};

export default {
  name: 'lista-transacoes-pix',
  components: { EmptyState },
  props: ['pedidos', 'openInfo', 'carregando', 'erro', 'recarregar'],
  methods: {
    chave(pedido) {
      return (pedido && pedido.status && pedido.status.chave) || '';
    },
    urgencia(pedido) {
      return URGENCIA[this.chave(pedido)] || 0;
    },
    corDoPonto(pedido) {
      return COR_DO_PONTO[this.chave(pedido)] || '#adb5bd';
    },
    /**
     * O tooltip nativo não rola nem quebra bem: a crítica do ERP pode vir com
     * milhares de caracteres (stack trace), e o balão fica inutilizável. Aqui vai
     * só o começo — o texto íntegro está no modal, com expandir e copiar.
     */
    resumoDoMotivo(pedido) {
      const motivo = pedido.motivo || '';

      return motivo.length > 220 ? `${motivo.slice(0, 220)}…` : motivo;
    },
    classeDoMotivo(pedido) {
      if (!pedido.motivo) {
        return 'is-vazio';
      }

      return this.urgencia(pedido) === 2 ? 'is-falha' : '';
    },
    convertDataHora(dataHora) {
      return dataHora
        ? moment(String(dataHora)).format('DD/MM/YYYY HH:mm')
        : '';
    },
  },
};
</script>
